"use client"

import { useCallback, useSyncExternalStore } from "react"
import { getQueryClient } from "@/lib/react-query"
import { getSession } from "next-auth/react"
import { useAuth } from "@/hooks/use-auth"
import { useTranslations } from "next-intl"


/**
 * Shared tenant-impersonation actions for cross-tenant roles (super_user, integrator).
 *
 * Consumed by the header tenant switcher and the tenant-management table so the
 * impersonate / stop-impersonate flow lives in one place. On success the page is
 * reloaded so the updated server context takes effect everywhere.
 */

// Module-level in-flight state shared across every useImpersonation() instance.
// The dashboard mounts the header switcher AND the tenant-management table at the
// same time, each with its own hook instance; a per-instance `switching` flag would
// leave the other surface clickable and let two overlapping /impersonate calls fire.
// A shared flag prevents competing switches across controls in the same page.
let switchingInFlight = false
const listeners = new Set<() => void>()

function setSwitching(value: boolean) {
  switchingInFlight = value
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot() {
  return switchingInFlight
}

export function useImpersonation(options?: { returnTo: () => string }) {
  const tUi = useTranslations("Feedback.UseImpersonation")

  const { fetchWithAuth } = useAuth()
  const switching = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  const returnTo = options?.returnTo
  const applyContext = useCallback(async () => {
    await getQueryClient().cancelQueries()
    getQueryClient().clear()
    await getSession()
    if (returnTo) window.location.replace(returnTo())
    else window.location.reload()
  }, [returnTo])

  const impersonate = useCallback(
    async (tenantId: string) => {
      if (switchingInFlight) return
      setSwitching(true)
      try {
        const response = await fetchWithAuth("/api/auth/impersonate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tenant_id: tenantId }),
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: tUi("unknownError") }))
          throw new Error(errorData.error || "Failed to impersonate tenant")
        }

        // applyContext reloads on success, so we intentionally leave the in-flight
        // flag set to keep every switch control disabled until the reload happens.
        await applyContext()
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to switch tenant"
        console.error("Error impersonating tenant:", error)
        alert(`Failed to switch tenant: ${message}`)
        setSwitching(false)
      }
    },
    [fetchWithAuth, applyContext, tUi],
  )

  const stopImpersonation = useCallback(async () => {
    if (switchingInFlight) return
    setSwitching(true)
    try {
      const response = await fetchWithAuth("/api/auth/stop-impersonate", {
        method: "POST",
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: tUi("unknownError") }))
        throw new Error(errorData.error || "Failed to stop impersonation")
      }

      await applyContext()
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to stop impersonation"
      console.error("Error stopping impersonation:", error)
      alert(`Failed to stop impersonation: ${message}`)
      setSwitching(false)
    }
  }, [fetchWithAuth, applyContext, tUi])

  return { switching, impersonate, stopImpersonation }
}
